/**
 * The one line in the release flow that acknowledges the flow runs on machines,
 * and the one control that turns them off.
 *
 * Everything else about the VMs belongs to the host pages. What cannot go there is
 * the answer to "how long have I got?" — the system stops itself when nothing has
 * run for a while, and an operator part-way through a release needs to know that
 * without learning that there are two timers on two machines. So there is one
 * timer here, the earliest of them, and one button that ends the session
 * deliberately instead of waiting the two hours out.
 *
 * It sits at the foot of the page because it is never the reason anyone opened it:
 * the release is. A bar at the top would make the machines the first thing read,
 * which is the habit this change exists to break.
 */
import { Link as RouterLink } from 'react-router-dom';
import {
    Button,
    Card,
    CardContent,
    CircularProgress,
    Link,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import BedtimeOutlined from '@mui/icons-material/BedtimeOutlined';
import FiberManualRecord from '@mui/icons-material/FiberManualRecord';
import { HOST_ROLES, INSTRUCTION_KIND, type HostRole } from '@finvari/deployer';
import { CAPTION_TICK_MS, useInstructionAction, useNow, useRecentInstructions, type InstructionAction } from '../hooks';
import { inflightRows, isInflightOrRecent } from '../lib/instructionActivity';
import { sleepText, type SystemView } from '../lib/releaseSystem';
import { ActivityAlert } from './ActivityAlert';

/** Why Sleep is held back while the system is working. */
const BUSY_REASON =
    'Something is running. The system refuses to stop while an instruction is in flight; it sleeps on its own once everything finishes.';

/** Why Sleep is held back before the instruction histories have arrived. */
const READING_REASON =
    'Still reading what is running. An empty history looks exactly like an idle system until the first snapshot lands, and a stop the host would refuse is not a click worth offering.';

export function SystemStatusBar({ system }: { system: SystemView }) {
    // `host.stop-vm` rather than the stop callable: it is the graceful path, it
    // appears in the instruction history like everything else the operator does, and
    // the host refuses it while work is running instead of killing that work.
    const prepareStop = useInstructionAction('prepare', INSTRUCTION_KIND.hostStopVm);
    const releaseStop = useInstructionAction('release', INSTRUCTION_KIND.hostStopVm);
    const stops: Record<HostRole, InstructionAction> = { prepare: prepareStop, release: releaseStop };
    const prepareInstructions = useRecentInstructions('prepare');
    const releaseInstructions = useRecentInstructions('release');
    const now = useNow(CAPTION_TICK_MS);
    const busy = [...inflightRows(prepareInstructions.value), ...inflightRows(releaseInstructions.value)];
    const reading = prepareInstructions.loading || releaseInstructions.loading;

    // Every online host, not just the phase's own: the point of one button is that
    // the operator does not have to know which machines are up.
    const sleep = () => {
        for (const role of system.online) stops[role].run();
    };

    const sending = system.online.some((role) => stops[role].sending);
    // Reading first: the windows start empty, so "nothing is in flight" is what a
    // pending subscription and an idle system both look like (`Loadable` in `hooks`).
    // Offering the button there hands the operator a stop the host rejects.
    const reason = reading ? READING_REASON : busy.length > 0 ? BUSY_REASON : undefined;
    const recentStops = HOST_ROLES.flatMap((role) => {
        const { latest, activity } = stops[role];
        return latest && activity && isInflightOrRecent(latest, now) ? [{ role, row: latest, line: activity }] : [];
    });
    const failures = HOST_ROLES.flatMap((role) => {
        const { error } = stops[role];
        return error ? [{ role, message: error }] : [];
    });
    const button = (
        <Button
            size="small"
            color="inherit"
            startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <BedtimeOutlined />}
            onClick={sleep}
            disabled={sending || !!reason || system.online.length === 0}
        >
            Sleep now
        </Button>
    );

    return (
        <Card variant="outlined">
            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap>
                    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                            <FiberManualRecord sx={{ fontSize: 10, color: 'success.main' }} />
                            <Typography variant="body2">{sleepText(system)}</Typography>
                        </Stack>
                        {/*
                          * The flow's only pointer at the machines. A caption, not
                          * cards: the per-machine controls, health checks and
                          * instruction history are what the host pages are for, and
                          * putting them here is what made the release a tour of the VMs.
                          */}
                        <Typography variant="caption" color="text.secondary">
                            Troubleshooting:{' '}
                            <Link component={RouterLink} to="/hosts/prepare">
                                prepare host
                            </Link>
                            {' · '}
                            <Link component={RouterLink} to="/hosts/release">
                                release host
                            </Link>
                        </Typography>
                    </Stack>
                    <Stack spacing={0.5} alignItems="flex-end" sx={{ flexShrink: 0 }}>
                        {reason ? <Tooltip title={reason}><span>{button}</span></Tooltip> : button}
                        {/*
                          * A stop takes a minute to show up as anything: the host keeps
                          * beating until systemd gets to it. Without the report the
                          * button reads as dead and gets pressed again. Only a recent
                          * one — the newest stop of a host that has been up for a week
                          * is last week's, and reporting it reads as this click's.
                          */}
                        {recentStops.map(({ role, row, line }) => (
                            <ActivityAlert key={role} host={role} row={row} line={line} variant="inline" />
                        ))}
                        {failures.map(({ role, message }) => (
                            <Typography key={role} variant="caption" color="error">
                                {message}
                            </Typography>
                        ))}
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}
